// Route guard for the developer console (simple-mode D30).
//
// Shell.tsx routes every developer page as a child of:
//
//   <Route element={<RequireDeveloperMode />}>
//     <Route path="/triggers" element={<TriggersView />} />
//     …
//   </Route>
//
// When Developer mode is not effective (preference off, or the plan lacks the
// `developer_mode` entitlement) the child never renders: the visitor lands on
// /apps?notice=developer-mode, and Apps shows a one-line notice naming the reason.
import { useEffect, useState } from 'react'
import { Link, Navigate, Outlet, useSearchParams } from 'react-router-dom'
import { useMode } from '../context/ModeContext'
import { Icon } from './Icon'
import { Loading } from './ui'

export const DEVELOPER_NOTICE_PARAM = 'notice'
export const DEVELOPER_NOTICE_VALUE = 'developer-mode'
const REDIRECT = `/apps?${DEVELOPER_NOTICE_PARAM}=${DEVELOPER_NOTICE_VALUE}`

export function RequireDeveloperMode() {
  const { loading, effective } = useMode()
  // Wait for the answer: a developer deep-linking to /playground must not be bounced
  // while /me/preferences and /entitlements are still in flight.
  if (loading) return <Loading />
  if (!effective) return <Navigate to={REDIRECT} replace />
  return <Outlet />
}

/**
 * One-line, dismissible notice for a redirected developer deep link. Shown once: the
 * `?notice=` parameter is stripped from the URL on first render, so a reload or a
 * later visit to /apps does not repeat it.
 */
export function DeveloperModeNotice() {
  const { entitled } = useMode()
  const [params, setParams] = useSearchParams()
  const [show, setShow] = useState(() => params.get(DEVELOPER_NOTICE_PARAM) === DEVELOPER_NOTICE_VALUE)

  useEffect(() => {
    if (params.get(DEVELOPER_NOTICE_PARAM) === DEVELOPER_NOTICE_VALUE) {
      const next = new URLSearchParams(params)
      next.delete(DEVELOPER_NOTICE_PARAM)
      setParams(next, { replace: true })
    }
  }, [params, setParams])

  if (!show) return null
  return (
    <div className="mode-notice" role="status">
      <Icon name="lock" size={14} />
      <span>
        {entitled ? (
          <>That page is part of Developer mode. Turn it on in <Link to="/account">Account</Link>.</>
        ) : (
          <>That page is part of Developer mode, which isn’t included in this workspace’s plan.</>
        )}
      </span>
      <button type="button" className="link-btn push-right" aria-label="Dismiss notice" onClick={() => setShow(false)}>
        <Icon name="x" size={14} />
      </button>
    </div>
  )
}
